import elephantLogomark from "$assets/elephant-logomark.svg?url";
import { useAuth0 } from "@auth0/auth0-react";
import { Badge, Box, ButtonBase, styled, Typography } from "@material-ui/core";
import { Link, useLocation } from "react-router-dom";
import {
  getCurrentTab,
  linkToEpocusHome,
  linkToEpocusSettings,
} from "../links";
import { EpocusAvatar } from "./epocus-avatar";
import { useVisibleNavItems } from "./epocus-nav-items";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_INACTIVE,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";
import { epocusTheme } from "./epocus-theme-provider";

const SIDEBAR_WIDTH = 220;

const Root = styled(Box)({
  // Hidden on phone, visible on tablet+
  display: "none",
  [epocusTheme.breakpoints.up("sm")]: {
    display: "flex",
    flexDirection: "column",
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    borderRight: `1px solid ${EPOCUS_BORDER}`,
    backgroundColor: EPOCUS_WHITE,
  },
});

const LogoArea = styled(Link)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "20px 20px 8px",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
});

const LogoBox = styled(Box)({
  width: 36,
  height: 36,
  borderRadius: 10,
  backgroundColor: EPOCUS_ICON_ACTIVE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const LogoImg = styled("img")({
  width: 22,
  height: 22,
  filter: "brightness(0) invert(1)",
});

const BrandText = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const BrandTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: rem(20),
  lineHeight: 1.2,
});

const NavList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "12px 8px",
  flex: 1,
});

const NavItem = styled(ButtonBase)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 12px",
  borderRadius: 8,
  width: "100%",
  justifyContent: "flex-start",
  minHeight: 44,
  color: EPOCUS_INACTIVE,
  fontSize: rem(16),
  textDecoration: "none",
  transition: "background-color 150ms, color 150ms",
  "&:hover": {
    backgroundColor: EPOCUS_BG,
  },
  "&.active": {
    color: EPOCUS_ICON_ACTIVE,
    backgroundColor: EPOCUS_BG,
    fontWeight: 600,
  },
});

const UserArea = styled(ButtonBase)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "16px 20px",
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  width: "100%",
  textAlign: "left",
  "&:hover": {
    backgroundColor: EPOCUS_BG,
  },
});

const UserName = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 600,
  lineHeight: 1.3,
});

export const EpocusSidebar: FC<{
  reviewCount?: number;
  referralCount?: number;
}> = ({ reviewCount = 0, referralCount = 0 }) => {
  const { user } = useAuth0();
  const { pathname } = useLocation();
  const currentTab = getCurrentTab(pathname);
  const navItems = useVisibleNavItems();

  const badgeCounts: Record<"review" | "referral", number> = {
    review: reviewCount,
    referral: referralCount,
  };

  return (
    <Root>
      <LogoArea to={linkToEpocusHome()} aria-label="Go to home">
        <LogoBox>
          <LogoImg src={elephantLogomark} alt="" />
        </LogoBox>
        <BrandText>
          <BrandTitle>ePOCUS</BrandTitle>
        </BrandText>
      </LogoArea>

      <NavList>
        {navItems.map(({ label, path, icon: Icon, badgeKey }) => {
          const href = path();
          const active = currentTab === href;
          const badgeCount = badgeKey ? badgeCounts[badgeKey] : 0;
          return (
            <NavItem
              key={href}
              // @ts-expect-error -- MUI v4 styled() drops component/to from ButtonBase type
              component={Link}
              to={href}
              className={active ? "active" : undefined}
            >
              {badgeCount > 0 ? (
                <Badge
                  badgeContent={badgeCount}
                  color="error"
                  overlap="rectangular"
                >
                  <Icon />
                </Badge>
              ) : (
                <Icon />
              )}
              {label}
            </NavItem>
          );
        })}
      </NavList>

      <UserArea
        // @ts-expect-error -- MUI v4 styled() drops component/to from ButtonBase type
        component={Link}
        to={linkToEpocusSettings()}
      >
        <EpocusAvatar name={user?.name} />
        <Box>
          <UserName>{user?.name ?? "Unknown"}</UserName>
        </Box>
      </UserArea>
    </Root>
  );
};
